import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import {
  AI_EDITOR_MODEL_KEY,
  AI_EDITOR_PROVIDER_KEY,
  AI_EDITOR_SETTINGS_EVENT,
  DEFAULT_AI_EDITOR_MODEL,
  DEFAULT_AI_EDITOR_PROVIDER,
  normalizeAiEditorModel,
  normalizeAiEditorProvider,
  readAndMigrateStoredAiEditorModel,
  readStoredAiEditorProvider,
} from '../api/clipEditorSettings';
import { ChatProvider } from '../api/structs/providers';

interface AiEditorSettings {
  provider: ChatProvider;
  model: string;
  setProvider: Dispatch<SetStateAction<ChatProvider>>;
  setModel: Dispatch<SetStateAction<string>>;
}

const safeReadProvider = (): ChatProvider => {
  try {
    return readStoredAiEditorProvider(window.localStorage);
  } catch {
    return DEFAULT_AI_EDITOR_PROVIDER;
  }
};

const safeReadModel = (): string => {
  try {
    return readAndMigrateStoredAiEditorModel(window.localStorage);
  } catch {
    return DEFAULT_AI_EDITOR_MODEL;
  }
};

const notifySettingsChanged = () => {
  window.dispatchEvent(new Event(AI_EDITOR_SETTINGS_EVENT));
};

const writeSetting = (key: string, value: string) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    notifySettingsChanged();
  } catch (error) {
    console.error('Failed to save AI editor settings:', error);
  }
};

export function useAiEditorSettings(): AiEditorSettings {
  const [provider, setProviderState] = useState<ChatProvider>(safeReadProvider);
  const [model, setModelState] = useState<string>(safeReadModel);

  const setProvider = useCallback<Dispatch<SetStateAction<ChatProvider>>>((next) => {
    const resolved = normalizeAiEditorProvider(typeof next === 'function' ? next(provider) : next);
    writeSetting(AI_EDITOR_PROVIDER_KEY, resolved);
    setProviderState(resolved);
  }, [provider]);

  const setModel = useCallback<Dispatch<SetStateAction<string>>>((next) => {
    const resolved = normalizeAiEditorModel(typeof next === 'function' ? next(model) : next);
    writeSetting(AI_EDITOR_MODEL_KEY, resolved);
    setModelState(resolved);
  }, [model]);

  useEffect(() => {
    const syncFromStorage = () => {
      setProviderState(safeReadProvider());
      setModelState(safeReadModel());
    };

    window.addEventListener('storage', syncFromStorage);
    window.addEventListener(AI_EDITOR_SETTINGS_EVENT, syncFromStorage);
    return () => {
      window.removeEventListener('storage', syncFromStorage);
      window.removeEventListener(AI_EDITOR_SETTINGS_EVENT, syncFromStorage);
    };
  }, []);

  return {
    provider: normalizeAiEditorProvider(provider),
    model: normalizeAiEditorModel(model),
    setProvider,
    setModel,
  };
}
